import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useIncidentDetail from '../../hooks/useIncidentDetail';
import CustomLoader from '../shared/CustomLoader';
import Message from '../shared/Message';
import { Badge, Button, Card, Col, Container, ListGroup, Row } from 'react-bootstrap';
import {FaArrowLeft, FaMapMarkedAlt, FaUserShield} from 'react-icons/fa'
import IncidentUpdateForm from '../forms/IncidentUpdateForm';
import FsLightbox from 'fslightbox-react';

const IncidentDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    
    const [messageData, setMessageData] = useState({
        show: false,
        text: '',
        variant: ''
    });

    const handleMessage = React.useCallback((text, variant = 'danger') => {
        setMessageData({show: true, text, variant});
    }, []); // Empty array means it's created only once

    const handleCloseMessage = () => setMessageData({...messageData, show: false});

    const { incident, loading, submitting, updateIncident } = useIncidentDetail(id, handleMessage);

    const onUpdateReport = async (data) => {
        const result = await updateIncident(data);
        if (result.success){
            handleMessage("Incident updated successfully!", "success");
        }
    }

    // Lightbox State 
    const [toggler, setToggler] = useState(false);
    const [productIndex, setProductIndex] = useState(0);

    // Helper to open lightbox at a specific image 
    const openLightboxOnIndex = (index) => {
        setProductIndex(index);
        setToggler(!toggler);
    }

    if (loading) return <CustomLoader />;

  return (
    <Container className='py-4'>

        <Message 
                variant={messageData.variant} 
                message={messageData.text} 
                show={messageData.show} 
                handleClose={handleCloseMessage}
            />

        <Button variant='link' onClick={() => navigate(-1)} className='mb-3 text-decoration-none p-0 text-dark'>
            <FaArrowLeft /> Back to Dashboard
        </Button>

        {/** The Lightbox Component */}
        <FsLightbox
            toggler={toggler}
            sources={incident.attachments || []}
            sourceIndex={productIndex}
        />

        <Row className='g-4'>
            {/** Main Content Column */}

            <Col lg={8}>
                <Card className='border-0 shadow-sm mb-4'>
                    <Card.Body className='p-4'>
                        <div className='d-flex justify-content-between align-items-center mb-4'>
                            <h3 className="fw-bold mb-0">Incident #{incident.id}</h3>
                            <Badge 
                                bg={incident.status === 'pending' ? 'warning' : 'success'} className='px-3 py-2 fs-6'>
                                    {incident.status.toUpperCase()}
                                </Badge>
                        </div>

                        <h5 className="fw-bold">Description</h5>
                        <p className="text-muted mb-4" style={{ whiteSpace: 'pre-wrap'}}>
                            {incident.description}
                        </p>

                        <h5 className="fw-bold">Action Taken</h5>
                        <div className="bg-light p-3 rounded mb-4">
                            {incident.action_taken || 'No action has been recorded yet.'}
                        </div>

                        <h5 className="fw-bold mb-3">Evidence & Photos</h5>
                        <Row className='g-2'>
                            {incident.attachments && incident.attachments.length > 0 ? (
                                incident.attachments.map((img, idx) => (
                                    <Col key={idx} xs={4} md={3}>
                                        <Card.Img src={img} className="rounded shadow-sm border" />
                                    </Col>
                                ))
                            ) : (
                                <Col><p className='text-muted small'>No photos uploaded for this report.</p></Col>
                            )}
                        </Row>

                        <IncidentUpdateForm
                            currentStatus={incident.status}
                            currentAction={incident.action_taken}
                            onUpdate={onUpdateReport}
                            submitting={submitting}
                        />
                    </Card.Body>
                </Card>
            </Col>

            {/** Sidebar Info Column */}
            <Col lg={4}>
                <Card className='border-0 shadow-sm'>
                    <ListGroup variant='flush'>
                        <ListGroup.Item className='py-3'>
                            <div className="text-muted small mb-1"><FaMapMarkedAlt className='me-2' />SITE</div>
                            <div className="fw-bold">{incident.duty_log?.schedule?.site?.name}</div>
                        </ListGroup.Item>
                        <ListGroup.Item className='py-3'>
                            <div className="text-muted small mb-1"><FaUserShield className='me-2' />REPORTED BY</div>
                            <div className="fw-bold">{incident.officer?.name}</div>
                        </ListGroup.Item>
                        <ListGroup.Item className='py-3'>
                            <div className="text-muted small mb-1">DATE & TIME</div>
                            <div className="fw-bold">{new Date(incident.created_at).toLocaleString()}</div>
                        </ListGroup.Item>
                    </ListGroup>
                </Card>
            </Col>
        </Row>
    </Container>
  )
}

export default IncidentDetail